// src/components/scratch/ScratchRiskValueReport.jsx
import React, { useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import Spinner from "../ui/Spinner";
import ResponsibleGamblingDisclaimer from "./ResponsibleGamblingDisclaimer";
import { runRiskValueReport, formatMoney, formatRatioPct, formatScore } from "../../services/scratchService";

/**
 * Scratch-Off Risk & Value Report — the premium analytics feature.
 *
 * Visible to ELITE-tier users on the dashboard. Costs 1 credit per
 * successful run; failed runs don't consume credits (backend debits only
 * after a successful generate).
 *
 * IMPORTANT — language policy:
 *
 *   ✗ Never used:  "buy this ticket", "purchase this game",
 *                  "recommended purchase", "guaranteed",
 *                  "best ticket to buy", "you should spend X dollars"
 *
 *   ✓ Always used: "higher relative value", "lower relative value",
 *                  "stronger remaining prize profile",
 *                  "weaker remaining prize profile",
 *                  "public prize-inventory data",
 *                  "entertainment purposes only",
 *                  "does not improve or guarantee your odds",
 *                  "scratch-off outcomes are random"
 *
 * The component never renders a "buy" CTA on individual games. The labels
 * surfaced are the structured `relativeValueLabel` and `prizeProfileLabel`
 * fields the backend ships in the DTO (it controls the copy in one place).
 *
 * Props:
 *   state            currently selected state (passed in by the dashboard)
 *   minPrice, maxPrice   optional ticket-price filter
 *   currentCredits   user's credit balance — used to disable the button
 *   onCreditChange   callback when a successful run debits a credit
 */
export default function ScratchRiskValueReport({
  state,
  minPrice,
  maxPrice,
  currentCredits = 0,
  onCreditChange,
}) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canRun = state && currentCredits >= 1 && !loading;

  const handleRun = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await runRiskValueReport({
        state,
        minTicketPrice: minPrice,
        maxTicketPrice: maxPrice,
      });
      setReport(data);
      if (typeof onCreditChange === "function") {
        onCreditChange(Math.max(0, currentCredits - 1));
      }
    } catch (e) {
      // 402 from backend → friendly message
      const reason = e?.response?.data?.reason;
      const msg = e?.response?.data?.message;
      if (reason === "NOT_ENOUGH_CREDITS") {
        setError("You don't have enough credits to run this report.");
      } else if (reason === "TIER_TOO_LOW") {
        setError("This report requires the Elite tier.");
      } else {
        setError(msg || "Couldn't generate the report. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3
            className="font-semibold text-[16px] flex items-center gap-2"
            style={{ color: "var(--txt)" }}
          >
            <Icon name="file-text" size={18} style={{ color: "var(--gold)" }} />
            Scratch-Off Risk &amp; Value Report
          </h3>
          <p className="text-[12px] mt-1" style={{ color: "var(--txt-2)" }}>
            Detailed breakdown of {state ? state : "the selected state"}'s active
            scratch-off games — higher- and lower-relative-value lists, based
            on today's public prize-inventory data.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap"
          style={{
            background: "var(--gold-dim)",
            color: "var(--gold)",
            border: "1px solid var(--gold-border)",
          }}
        >
          <Icon name="coin" size={10} />
          1 credit
        </span>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={handleRun}
          loading={loading}
          disabled={!canRun}
          size="sm"
        >
          <Icon name="sparkles" size={14} />
          {report ? "Re-run report" : "Generate report"}
        </Button>
        <span className="text-[11.5px]" style={{ color: "var(--txt-3)" }}>
          You have {currentCredits} credit{currentCredits === 1 ? "" : "s"}.
          {currentCredits < 1 && " Buy more credits to run this report."}
        </span>
      </div>

      {error && (
        <p
          className="mt-3 text-[12px] px-3 py-2 rounded-lg"
          style={{
            background: "var(--red-dim)",
            color: "var(--red)",
            border: "1px solid var(--red-border)",
          }}
        >
          {error}
        </p>
      )}

      {report && (
        <div className="mt-5 space-y-5">
          <ReportSummary report={report} />
          <ReportSection
            title="Higher relative value"
            subtitle="Games whose remaining prize profile is stronger than the state average today."
            iconName="trophy"
            iconColor="var(--gold)"
            entries={report.higherRelativeValue}
          />
          <ReportSection
            title="Lower relative value"
            subtitle="Games whose remaining prize profile is weaker than the state average today."
            iconName="ticket-off"
            iconColor="var(--txt-3)"
            entries={report.lowerRelativeValue}
          />

          {/* Mandatory non-recommendation notice */}
          <div
            className="rounded-lg px-3 py-2.5 text-[11.5px] leading-relaxed"
            style={{
              background: "var(--surf-2)",
              border: "1px solid var(--border)",
              color: "var(--txt-2)",
            }}
          >
            <strong style={{ color: "var(--txt)" }}>Notice. </strong>
            {report.notice}
          </div>

          {report.disclaimers?.length > 0 && (
            <ul className="space-y-1 text-[11px]" style={{ color: "var(--txt-3)" }}>
              {report.disclaimers.map((d) => (
                <li key={d} className="flex items-start gap-1.5">
                  <Icon name="info-circle" size={11} style={{ marginTop: 2, flexShrink: 0 }} />
                  {d}
                </li>
              ))}
            </ul>
          )}

          <ResponsibleGamblingDisclaimer />
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function ReportSummary({ report }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <SummaryStat label="State"    value={report.state} />
      <SummaryStat label="Games"    value={String(report.totalGamesAnalyzed)} />
      <SummaryStat label="Higher"   value={String(report.higherRelativeValue?.length || 0)} />
      <SummaryStat label="Lower"    value={String(report.lowerRelativeValue?.length || 0)} />
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div
      className="rounded-lg px-3 py-2.5 text-center"
      style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}
    >
      <div
        className="text-[10px] tracking-wider uppercase font-semibold"
        style={{ color: "var(--txt-3)" }}
      >
        {label}
      </div>
      <div className="font-semibold mt-1" style={{ color: "var(--txt)", fontSize: 18 }}>
        {value}
      </div>
    </div>
  );
}

function ReportSection({ title, subtitle, iconName, iconColor, entries }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={iconName} size={16} style={{ color: iconColor }} />
        <h4 className="font-semibold text-[14px]" style={{ color: "var(--txt)" }}>
          {title}
        </h4>
      </div>
      <p className="text-[11.5px] mb-3" style={{ color: "var(--txt-3)" }}>
        {subtitle}
      </p>
      {(!entries || entries.length === 0) ? (
        <p className="text-[12px] italic" style={{ color: "var(--txt-3)" }}>
          No games in this list for the current filter.
        </p>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <ReportRow key={e.gameId} entry={e} />
          ))}
        </div>
      )}
    </section>
  );
}

function ReportRow({ entry }) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-lg"
      style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center"
        style={{ background: "var(--ink-dim)", border: "1px solid var(--ink-border)", color: "var(--ink)" }}
      >
        <span className="font-bold leading-none" style={{ fontSize: 13 }}>
          {formatScore(entry.scratchScore)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate text-[13px]" style={{ color: "var(--txt)" }}>
            {entry.gameName || `Game #${entry.gameNumber}`}
          </span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider"
            style={{ background: "var(--surf-3)", color: "var(--txt-2)" }}
          >
            {entry.prizeProfileLabel}
          </span>
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "var(--txt-2)" }}>
          {formatMoney(entry.ticketPrice)} ticket ·{" "}
          {formatRatioPct(entry.topPrizeRemainingRatio)} of top prizes remain ·{" "}
          {entry.relativeValueLabel}
        </div>
      </div>
    </div>
  );
}
